import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable, firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { SourceReference } from '../../shared/models/common/source-reference.model';
import { ChatActionEvent, ChatDocumentRef } from '../models/chat-document-ref.model';
import { REF_DOCUMENT_SHORT_NAME, REF_TRANSACTION_ID } from '../models/ref-deep-link';
import { workspaceBase } from '../transport/api-base';

const OPEN_DOCUMENT_ACTIONS = new Set(['open_document', 'open_document_ref']);

/** Ceiling on one transaction's documents: a deal carries a handful, so this only bounds a runaway match. */
const TRANSACTION_DOCUMENTS_LIMIT = 200;

interface ListedWorkspaceDocument {
  id: string;
  originalName?: string;
  library?: string;
  documentType?: string;
  metadata?: Record<string, string>;
}

/** Subset of the workspace document-store `GET /documents` response we read coordinates from. */
interface WorkspaceDocumentsResponse {
  documents?: ListedWorkspaceDocument[];
}

/** Where a semeris document lives in the workspace document-store, once resolved. */
export interface WorkspaceDocumentLocation {
  /** Workspace-AI document UUID — the id `read_document` / `search_documents` take. */
  documentId: string;
  library?: string;
}

/** The composite key sdocs uses in place of a document id. */
export interface SemerisDocumentCoordinates {
  libraryTransactionId: string;
  documentShortName: string;
}

interface WorkspaceLibrariesResponse {
  libraries?: { id: string; displayName?: string }[];
}

/** Display metadata for a cited workspace document, as the citation popover shows it. */
export interface WorkspaceDocumentMeta {
  document?: string;
  library?: string;
  documentType?: string;
}

@Injectable({ providedIn: 'root' })
export class WorkspaceDocResolverService {
  private readonly http = inject(HttpClient);
  private readonly metaLookups = new Map<string, Promise<WorkspaceDocumentMeta | null>>();
  private readonly libraryNameLoads = new Map<string, Promise<void>>();
  private readonly libraryNames = new Map<string, string>();

  /**
   * Extracts a ChatDocumentRef from a chat ActionBlock.
   *
   * Returns null (with a console.warn) only when the action isn't a document-open
   * action or carries no documentId. The semeris coordinates (documentShortName +
   * transactionId) may be absent here — the chat action payload doesn't always
   * carry them — so callers should back-fill them via `withCoordinates` before
   * opening the viewer.
   */
  public parse(action: ChatActionEvent): ChatDocumentRef | null {
    const p = action.payload;

    // Chart/report artifacts are routed by their own ids — never treat them as a
    // document open, even if a payload someday carries a documentId alongside.
    if (p.chartId || p.reportId) {
      return null;
    }

    const documentId = p.documentId;

    // Treat any action carrying a documentId as a document open — the artifact-card button
    // action string varies (open_document / view_document / …), but a documentId payload is
    // unambiguous (every other artifact family carries its own id key instead).
    if (!documentId) {
      if (OPEN_DOCUMENT_ACTIONS.has(action.action)) {
        console.warn('[WorkspaceDocResolver] document action has no payload.documentId', action);
      }
      return null;
    }

    // The payload often already carries the semeris coordinates — read the key aliases the
    // ai-server / citation refs use (the viewer's `transactionId` is the `libraryTransactionId`,
    // and a clicked ref badge contributes kebab-cased `ref-*` keys from its data attributes) so we
    // skip the cross-origin `/documents` back-fill whenever the data is already present.
    return {
      documentId,
      documentShortName: p.documentShortName ?? p.shortName ?? p[REF_DOCUMENT_SHORT_NAME],
      transactionId: p.transactionId ?? p.libraryTransactionId ?? p[REF_TRANSACTION_ID],
      snippet: p.snippet,
    };
  }

  /**
   * Back-fills a ref's missing semeris coordinates from the workspace document's upload metadata.
   *
   * The sdocs ingest stamps { libraryTransactionId, documentShortName } on each uploaded document;
   * ai-server reads the same keys on search. When a citation action arrives without them, we read
   * them back from `GET /api/workspaces/{id}/documents?ids={docId}` (surfacing libraryTransactionId
   * as transactionId, per the consumer contract) so the viewer can still open.
   *
   * Best-effort: returns the ref unchanged when it already has both coordinates, when no workspace
   * is selected, or when the lookup fails — the caller then treats an incomplete ref as a no-op.
   */
  public withCoordinates(ref: ChatDocumentRef, workspaceId: string | undefined): Observable<ChatDocumentRef> {
    if ((ref.documentShortName && ref.transactionId) || !workspaceId) {
      return of(ref);
    }

    const url = `${workspaceBase(workspaceId)}/documents`;
    const params = new HttpParams().set('ids', ref.documentId).set('limit', 1);

    // `withCredentials` so the gateway authenticates this cross-origin call and returns
    // credentialed-CORS headers — the agent/chat APIs already do this; the lookup used to 401
    // + CORS-fail without it.
    return this.http.get<WorkspaceDocumentsResponse>(url, { params, withCredentials: true }).pipe(
      map((res) => {
        const metadata: Record<string, string> = res.documents?.find((d) => d.id === ref.documentId)?.metadata ?? {};
        return {
          ...ref,
          documentShortName: ref.documentShortName ?? metadata.documentShortName,
          transactionId: ref.transactionId ?? metadata.libraryTransactionId,
        };
      }),
      catchError((err: unknown) => {
        console.warn('[WorkspaceDocResolver] coordinate lookup failed; opening without back-filled coordinates', err);
        return of(ref);
      }),
    );
  }

  /** Every workspace document ingested for one semeris transaction, keyed by `documentShortName`, in one request.
   *  Best-effort: a short name missing from the map has no workspace document, and must never be sent as an id. */
  public resolveTransactionDocuments(
    libraryTransactionId: string,
    workspaceId: string | undefined,
  ): Observable<ReadonlyMap<string, WorkspaceDocumentLocation>> {
    if (!workspaceId || !libraryTransactionId) {
      return of(new Map());
    }

    const url = `${workspaceBase(workspaceId)}/documents`;
    const params = new HttpParams()
      .set('fields', JSON.stringify({ 'upload:libraryTransactionId': libraryTransactionId }))
      .set('limit', TRANSACTION_DOCUMENTS_LIMIT);

    return this.http.get<WorkspaceDocumentsResponse>(url, { params, withCredentials: true }).pipe(
      map((res) => locationsByShortName(res.documents ?? [])),
      catchError((err: unknown) => {
        console.warn('[WorkspaceDocResolver] transaction lookup failed; continuing without document context', err);
        return of(new Map<string, WorkspaceDocumentLocation>());
      }),
    );
  }

  /**
   * Loads a cited document's display metadata for the citation popover.
   *
   * Only called for refs the streaming harvest didn't already cover (a conversation restored from
   * history), so misses are cached too — a repeated hover shouldn't re-request a document that
   * isn't in the workspace.
   */
  public loadDocumentMeta(docId: string, workspaceId: string | undefined): Promise<WorkspaceDocumentMeta | null> {
    if (!workspaceId) {
      return Promise.resolve(null);
    }

    const cacheKey = `${workspaceId}|${docId}`;
    const cached = this.metaLookups.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    const url = `${workspaceBase(workspaceId)}/documents`;
    const params = new HttpParams().set('ids', docId).set('limit', 1);

    const request$ = this.http.get<WorkspaceDocumentsResponse>(url, { params, withCredentials: true }).pipe(
      map((res): WorkspaceDocumentMeta | null => {
        const doc = res.documents?.find((d) => d.id === docId);
        return doc ? { document: doc.originalName, library: doc.library, documentType: doc.documentType } : null;
      }),
      catchError((err: unknown) => {
        console.warn('[WorkspaceDocResolver] document metadata lookup failed', err);
        return of(null);
      }),
    );

    const lookup = firstValueFrom(request$);
    this.metaLookups.set(cacheKey, lookup);
    return lookup;
  }

  /**
   * Display name for a workspace library, for the citation popover's badge.
   *
   * Synchronous because the popover renders it inline, so the first call for a workspace returns
   * `undefined` and starts the one-shot list fetch — the badge is omitted until it lands, then
   * every later citation has it. Deliberately not derived from the id: prettifying a slug would
   * mis-case real names ("Clo Notices" for "CLO Notices").
   */
  public libraryName(libraryId: string, workspaceId: string | undefined): string | undefined {
    if (!workspaceId) {
      return undefined;
    }
    if (!this.libraryNameLoads.has(workspaceId)) {
      this.libraryNameLoads.set(workspaceId, this.loadLibraryNames(workspaceId));
    }
    return this.libraryNames.get(libraryId);
  }

  private loadLibraryNames(workspaceId: string): Promise<void> {
    const url = `${workspaceBase(workspaceId)}/libraries`;
    return firstValueFrom(this.http.get<WorkspaceLibrariesResponse>(url, { withCredentials: true }))
      .then((res) => {
        for (const library of res.libraries ?? []) {
          if (library.displayName) {
            this.libraryNames.set(library.id, library.displayName);
          }
        }
      })
      .catch((err: unknown) => {
        console.warn('[WorkspaceDocResolver] library name lookup failed; citation badges omit the library', err);
      });
  }

  /**
   * Converts a ChatDocumentRef into a SourceReference for viewer fuzzy-matching.
   *
   * When a snippet is present, the viewer's SourceReferenceController matches
   * paragraphText first and falls back to searchTerm — so we populate both.
   * Returns null for the open-document-only case (no snippet).
   */
  public toSourceReference(ref: ChatDocumentRef): SourceReference | null {
    if (!ref.snippet || !ref.documentShortName || !ref.transactionId) {
      return null;
    }

    return {
      documentShortName: ref.documentShortName,
      libraryTransactionId: ref.transactionId,
      paragraphText: ref.snippet,
      searchTerm: ref.snippet,
    };
  }
}

/** The listing is newest first, so the first match per short name is the latest ingest. */
function locationsByShortName(documents: readonly ListedWorkspaceDocument[]): Map<string, WorkspaceDocumentLocation> {
  const locations = new Map<string, WorkspaceDocumentLocation>();
  for (const doc of documents) {
    const shortName = doc.metadata?.documentShortName;
    if (shortName && !locations.has(shortName)) {
      locations.set(shortName, { documentId: doc.id, library: doc.library });
    }
  }
  return locations;
}
